import React from 'react';
import { Box, Link as MaterialLink, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import OpenInNewOutlined from '@mui/icons-material/OpenInNewOutlined';
import classNames from 'classnames';
import { getTextContentURL } from '../../Content/helper';
import CardOverlayBadge from '../../Card/CardOverlayBadge';
import ContentCover from '../../Content/ContentCover';
import Link from '../../Link';
import ContentTile from '../../ContentTile';
import RoomContentWrapper, {
  RoomContentWrapperProps,
} from './RoomContentWrapper';
import useDarkTheme from '../../../theme/styles/useDarkTheme';
import ContentTextRenderer from '../../ContentTextRenderer';

const useStyles = makeStyles((theme: Theme) => ({
  coverLink: {
    height: '100%',
    position: 'absolute',
    width: '100%',
    zIndex: 2,
  },
  darkTheme: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
  },
  largeFontSize: {
    fontSize: theme.typography.h6.fontSize,
  },
  withBorder: {
    borderBottom: '1px solid var(--info-color)',
    borderTop: '1px solid var(--info-color)',
  },
}));

export interface RoomTextContentProps
  extends Pick<RoomContentWrapperProps, 'cover' | 'rounded' | 'width'> {
  className?: string;
  darkTheme?: boolean;
  disablePadding?: boolean;
  hasBorder?: boolean;
  html?: boolean;
  largeFontSize?: boolean;
  onClick?: () => void;
  rounded?: boolean;
  text?: string;
}

const RoomTextContent = React.forwardRef<HTMLDivElement, RoomTextContentProps>(
  (props: RoomTextContentProps, ref) => {
    const classes = useStyles();
    const darkThemeClasses = useDarkTheme();

    const {
      className,
      cover,
      darkTheme = false,
      disablePadding = false,
      hasBorder,
      html,
      largeFontSize = false,
      onClick,
      rounded,
      text,
      width,
    } = props;

    const [url, isUrl] = getTextContentURL(text);

    const onContentClicked = () => {
      if (onClick) {
        onClick();
      }
    };

    if (isUrl && cover) {
      return (
        <RoomContentWrapper ref={ref} rounded={rounded} width={width}>
          <MaterialLink
            className={classes.coverLink}
            href={url}
            onClick={onContentClicked}
            rel="noopener noreferrer"
            target="_blank"
            underline="hover"
          >
            {/* this child is just to avoid a react warning */}
            &nbsp;
          </MaterialLink>
          <Box position="absolute" bottom={0} right={0} zIndex={1}>
            <CardOverlayBadge>
              <Box pl={0.5} pr={0.25} pt={0.5}>
                <Link
                  afterIcon={
                    <OpenInNewOutlined color="inherit" fontSize="small" />
                  }
                  className={darkThemeClasses.primaryTextLight}
                  text="Visit link"
                  variant="body2"
                />
              </Box>
            </CardOverlayBadge>
          </Box>
          <ContentCover cover={cover} />
        </RoomContentWrapper>
      );
    }

    return (
      <Box
        {...{ ref }}
        className={className}
        component="div"
        onClick={onContentClicked}
        role="listitem"
      >
        {cover && (
          <ContentTile rounded={rounded} width={width}>
            <ContentCover cover={cover} />
          </ContentTile>
        )}
        <Box
          className={classNames({
            [classes.darkTheme]: darkTheme,
            [classes.withBorder]: !darkTheme && hasBorder,
          })}
          mt={darkTheme && cover ? 2 : 0}
          p={disablePadding ? 0 : 4}
        >
          <ContentTextRenderer
            className={classNames({ [classes.largeFontSize]: largeFontSize })}
            html={html!}
            text={text!}
            whiteSpace={html ? undefined : 'pre-wrap'}
          />
        </Box>
      </Box>
    );
  }
);

export default RoomTextContent;
